import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import moment from 'moment'
import { COLORS } from '../../constants'

const CHART_FONT_FAMILY =
  "-apple-system, BlinkMacSystemFont, 'avenir next', avenir, 'helvetica neue', helvetica, ubuntu, roboto, noto, 'segoe ui', arial, sans-serif"

const SERIES_IDS = {
  PRESSURE: 'pressure',
  LOW_PRESSURE: 'lowPressure',
  HIGH_PRESSURE: 'highPressure',
  ALERT_PRESSURE: 'alertPressure',
  ALERT_THRESHOLD: 'alertThreshold',
  RESTORE_THRESHOLD: 'restoreThreshold',
}

const DEFAULT_VISIBLE_SERIES = {
  [SERIES_IDS.PRESSURE]: true,
  [SERIES_IDS.LOW_PRESSURE]: false,
  [SERIES_IDS.HIGH_PRESSURE]: false,
  [SERIES_IDS.ALERT_PRESSURE]: false,
  [SERIES_IDS.ALERT_THRESHOLD]: false,
  [SERIES_IDS.RESTORE_THRESHOLD]: false,
}

const toChartValue = value =>
  value != null && !isNaN(parseFloat(value))
    ? Number(parseFloat(value).toFixed(3))
    : null

const MIN_GAP_MS = 60 * 60 * 1000
const MIN_BAND_MS = 60 * 1000
const UNCORRECTED_BAND_COLOR = 'rgba(51, 51, 51, 0.15)'

// the usual time between readings
const getMedianSpacing = times => {
  const spacings = times
    .slice(1)
    .map((time, i) => time - times[i])
    .sort((a, b) => a - b)

  return spacings.length ? spacings[Math.floor(spacings.length / 2)] : 0
}

// a gap longer than this breaks the line rather than drawing across it. it's
// scaled to the data's usual spacing so smoothed data, or a monitor that
// reports less often than every 30 minutes, isn't broken at every point
const getGapThreshold = times =>
  Math.max(MIN_GAP_MS, getMedianSpacing(times) * 2)

// shaded x-axis bands over the time ranges whose readings weren't corrected
// for elevation. A lone uncorrected reading gives a range that starts and
// ends at the same time, so each band is at least minWidth wide to be visible
export const uncorrectedPlotBands = (ranges, minWidth) =>
  ranges.map(({ start, end }) => {
    const from = moment(start).valueOf()

    return {
      from,
      to: Math.max(moment(end).valueOf(), from + minWidth),
      color: UNCORRECTED_BAND_COLOR,
    }
  })

// [time, value] points for one field, leaving out points without a value.
// Readings (pressure, low/high) and alert messages (alertPressure) arrive as
// separate points, so each series only gets the points that carry its field -
// otherwise every alert message would put a null in the pressure line and
// break it
export const seriesPoints = (data, key) =>
  data
    .map(y => [moment(y.time).valueOf(), toChartValue(y[key])])
    .filter(([, value]) => value != null)

// Highcharts ends a line at a null point, so one goes in each gap
export const breakAtGaps = (points, gapThreshold) =>
  points.reduce((result, point, i) => {
    const previous = points[i - 1]

    if (previous && point[0] - previous[0] > gapThreshold) {
      result.push([(previous[0] + point[0]) / 2, null])
    }
    result.push(point)

    return result
  }, [])

const PressureMonitorChart = ({
  data,
  uncorrectedRanges,
  units,
  activateThreshold,
  deactivateThreshold,
  width,
  height,
}) => {
  // kept in state rather than left to Highcharts so the 30s data refresh,
  // which passes new options, doesn't reset what the user toggled
  const [visibleSeries, setVisibleSeries] = useState(DEFAULT_VISIBLE_SERIES)

  const pressurePoints = seriesPoints(data, 'pressure')
  // gaps are measured between readings only, so a long gap between them
  // still breaks the line even if alert messages arrived in it
  const pressureTimes = pressurePoints.map(([time]) => time)
  const gapThreshold = getGapThreshold(pressureTimes)
  // a lone uncorrected reading is shaded as wide as one reading's spacing
  const plotBands = uncorrectedPlotBands(
    uncorrectedRanges,
    Math.max(MIN_BAND_MS, getMedianSpacing(pressureTimes))
  )

  // only readings from v6.1+ packets carry a low/high over the sample window
  const hasLowHigh = data.some(
    y => y.lowPressure != null || y.highPressure != null
  )
  const rangeSeries = (id, name, key, color) => ({
    id,
    name,
    type: 'line',
    color,
    visible: visibleSeries[id],
    data: breakAtGaps(seriesPoints(data, key), gapThreshold),
  })

  // thresholds are drawn as flat series (not yAxis plotLines) so they get a
  // legend entry to toggle, and so the y-axis only stretches to fit them
  // while they're shown
  const thresholdSeries = (id, name, value, color) => {
    const startTime = pressurePoints[0][0]
    const endTime = pressurePoints[pressurePoints.length - 1][0]

    return {
      id,
      name,
      type: 'line',
      color,
      dashStyle: 'Dash',
      lineWidth: 2,
      marker: { enabled: false },
      enableMouseTracking: false,
      visible: visibleSeries[id],
      data: [
        [startTime, value],
        [endTime, value],
      ],
    }
  }

  const alertThresholdValue = toChartValue(activateThreshold)
  const restoreThresholdValue = toChartValue(deactivateThreshold)

  const series = [
    {
      id: SERIES_IDS.PRESSURE,
      // with low/high alongside, the pressure reading is the window's average
      name: hasLowHigh ? 'Average Pressure' : 'Pressure',
      type: 'line',
      color: COLORS.BLUE,
      visible: visibleSeries[SERIES_IDS.PRESSURE],
      data: breakAtGaps(pressurePoints, gapThreshold),
    },
  ]

  if (hasLowHigh) {
    series.push(
      rangeSeries(
        SERIES_IDS.LOW_PRESSURE,
        'Low Pressure',
        'lowPressure',
        COLORS.PURPLE
      ),
      rangeSeries(
        SERIES_IDS.HIGH_PRESSURE,
        'High Pressure',
        'highPressure',
        COLORS.ORANGE
      )
    )
  }

  // the pressure when each alert message (v7) was sent - separate events, so
  // markers rather than a line
  const alertPressurePoints = seriesPoints(data, 'alertPressure')
  if (alertPressurePoints.length) {
    series.push({
      id: SERIES_IDS.ALERT_PRESSURE,
      name: 'Alert Pressure',
      type: 'scatter',
      color: COLORS.DARK_PINK,
      marker: { symbol: 'diamond', radius: 5 },
      // scatter tooltips default to "x: ..., y: ..."; match the lines'
      tooltip: {
        pointFormat:
          '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y}</b><br/>',
      },
      visible: visibleSeries[SERIES_IDS.ALERT_PRESSURE],
      data: alertPressurePoints,
    })
  }

  if (pressurePoints.length && alertThresholdValue != null) {
    series.push(
      thresholdSeries(
        SERIES_IDS.ALERT_THRESHOLD,
        `Alert Threshold (${alertThresholdValue} ${units})`,
        alertThresholdValue,
        COLORS.DARK_RED
      )
    )
  }

  if (pressurePoints.length && restoreThresholdValue != null) {
    series.push(
      thresholdSeries(
        SERIES_IDS.RESTORE_THRESHOLD,
        `Restore Threshold (${restoreThresholdValue} ${units})`,
        restoreThresholdValue,
        COLORS.GREEN
      )
    )
  }

  const chartOptions = {
    credits: { enabled: false },
    exporting: { enabled: false },
    chart: {
      zoomType: 'x',
      width,
      height,
      style: { fontFamily: CHART_FONT_FAMILY },
    },
    title: { text: null },
    plotOptions: {
      series: {
        events: {
          legendItemClick: ev => {
            const { id } = ev.target.userOptions
            setVisibleSeries(current => ({ ...current, [id]: !current[id] }))
            // visibility comes from state via the `visible` option; letting
            // Highcharts also toggle it would undo the state-driven update
            ev.preventDefault()
          },
        },
      },
    },
    yAxis: {
      gridLineWidth: 1,
      gridLineDashStyle: 'longdash',
      labels: {
        format: `{value} ${units}`,
        style: { color: COLORS.BLACK_80, fontSize: '0.75rem' },
      },
      title: { text: null },
    },
    xAxis: {
      gridLineWidth: 1,
      gridLineDashStyle: 'longdash',
      type: 'datetime',
      labels: { style: { color: COLORS.BLACK_80, fontSize: '0.75rem' } },
      title: { text: null },
      crosshair: true,
      plotBands,
    },
    tooltip: {
      valueDecimals: 3,
      valueSuffix: ` ${units}`,
      headerFormat:
        "<span style='font-size: 0.875rem; margin-bottom: 0.875rem'>{point.key}</span><br />",
      style: { fontSize: '0.875rem', color: COLORS.BLACK_80 },
    },
    legend: {
      verticalAlign: 'top',
      itemStyle: { fontSize: '0.75rem', color: COLORS.BLACK_80 },
      itemHoverStyle: { color: COLORS.MOON_GRAY },
    },
    series,
  }

  return (
    <div className="PressureMonitorChart">
      <div className="mb3">
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        {plotBands.length > 0 && (
          <div
            className="flex items-center f7 mt1"
            style={{ color: COLORS.BLACK_80 }}
          >
            <span
              className="dib mr2 ba"
              style={{
                width: '1rem',
                height: '0.75rem',
                backgroundColor: UNCORRECTED_BAND_COLOR,
                borderColor: COLORS.MOON_GRAY,
              }}
            />
            Shaded periods were not corrected for elevation
          </div>
        )}
      </div>
    </div>
  )
}

PressureMonitorChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  // [{ start, end }] times whose readings weren't corrected for elevation,
  // shaded on the chart
  uncorrectedRanges: PropTypes.arrayOf(
    PropTypes.shape({ start: PropTypes.string, end: PropTypes.string })
  ),
  units: PropTypes.string,
  activateThreshold: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  deactivateThreshold: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  // fixed chart size; left unset, Highcharts sizes the chart to its container
  width: PropTypes.number,
  height: PropTypes.number,
}

PressureMonitorChart.defaultProps = {
  uncorrectedRanges: [],
  units: 'wc',
  activateThreshold: null,
  deactivateThreshold: null,
  width: null,
  height: null,
}

export default PressureMonitorChart
